import React, { useEffect, useState, useRef } from 'react';
import { SearchIcon, ChevronDownIcon, CheckIcon } from 'lucide-react';
export interface SelectOption {
  id: string;
  label: string;
  category?: string;
}
interface SearchableSelectProps {
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
}
export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = 'Search...',
  disabled = false
}: SearchableSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const selectedOption = options.find(opt => opt.id === value);
  const filteredOptions = options.filter(option => {
    const searchLower = searchQuery.toLowerCase();
    return option.label.toLowerCase().includes(searchLower) || option.category?.toLowerCase().includes(searchLower);
  });
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);
  const handleSelect = (optionId: string) => {
    onChange(optionId);
    setIsOpen(false);
    setSearchQuery('');
  };
  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setTimeout(() => inputRef.current?.focus(), 50);
      }
    }
  };
  return <div ref={containerRef} className="relative" data-id="element-308">
      {/* Trigger Button */}
      <button type="button" onClick={handleToggle} disabled={disabled} className={`w-full flex items-center justify-between gap-2 rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2.5 text-sm text-left transition-colors ${disabled ? 'bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed' : 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white hover:border-gray-400 dark:hover:border-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} aria-haspopup="listbox" aria-expanded={isOpen} data-id="element-309">
        <span className={selectedOption ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-500'} data-id="element-310">
          {selectedOption ? <span data-id="element-311">
              {selectedOption.label}
              {selectedOption.category && <span className="text-gray-400 dark:text-gray-500 ml-1.5" data-id="element-312">
                  ({selectedOption.category})
                </span>}
            </span> : placeholder}
        </span>
        <ChevronDownIcon className={`w-4 h-4 text-gray-400 dark:text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} data-id="element-313" />
      </button>

      {/* Dropdown */}
      {isOpen && <div className="absolute z-50 mt-2 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg overflow-hidden" data-id="element-314">
          {/* Search Input */}
          <div className="p-2 border-b border-gray-100 dark:border-gray-700" data-id="element-315">
            <div className="relative" data-id="element-316">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" data-id="element-317" />
              <input ref={inputRef} type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search..." className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-318" />
            </div>
          </div>

          {/* Options List */}
          <div className="max-h-60 overflow-y-auto" role="listbox" aria-label="Select options" data-id="element-319">
            {filteredOptions.length === 0 ? <div className="px-4 py-8 text-center text-sm text-gray-400 dark:text-gray-500" data-id="element-320">
                No results found
              </div> : filteredOptions.map(option => {
          const isSelected = option.id === value;
          return <button key={option.id} type="button" onClick={() => handleSelect(option.id)} className={`w-full flex items-center justify-between gap-2 px-4 py-2.5 text-sm text-left transition-colors ${isSelected ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700'}`} role="option" aria-selected={isSelected} data-id="element-321">
                    <div className="flex-1 min-w-0" data-id="element-322">
                      <div className="font-medium truncate" data-id="element-323">{option.label}</div>
                      {option.category && <div className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5" data-id="element-324">
                          {option.category}
                        </div>}
                    </div>
                    {isSelected && <CheckIcon className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0" data-id="element-325" />}
                  </button>;
        })}
          </div>
        </div>}
    </div>;
}